import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Action } from 'src/interfaces/action.interface';
import { LocalAction } from '../ManageAgent';

interface AgentActionsContextData {
  selectedActions: string[];
  loading: boolean;
  fetchActions: (filter?: string) => Promise<void>;
  addAction: (action: Action) => void;
  updateAction: (action: Action) => void;
  setSelectedActions: (actions: string[]) => void;
  getActionById: (id: string) => Promise<Action | LocalAction | undefined>;
  globalActions: Action[];
  filterActions: (filter: string) => void;
  getActionsToSave: () => (string | (Action & { originalId?: string }))[];
  resetActions: () => void;
}

const AgentActionsContext = createContext<AgentActionsContextData>(
  {} as AgentActionsContextData,
);

interface AgentActionsProviderProps {
  children: ReactNode;
  initialSelectedActions?: string[];
}

export function AgentActionsProvider({
  children,
  initialSelectedActions = [],
}: AgentActionsProviderProps) {
  const { get } = useAuthedRequest();

  const [globalActions, setGlobalActions] = useState<Action[]>([]);
  const [serverActions, setServerActions] = useState<Action[]>([]);
  const [localActions, setLocalActions] = useState<LocalAction[]>([]);
  const [selectedActions, setSelectedActions] = useState<string[]>(
    initialSelectedActions,
  );
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const fetchActions = useCallback(
    async (filter?: string) => {
      setLoading(true);

      setFilter(filter || '');

      try {
        const query = new URLSearchParams({
          size: '50',
          filters: JSON.stringify({
            scope: 'global|user',
            ...(filter && filter.length > 0 && { label: filter }),
          }),
        });

        const { items } = await get(`/actions?${query.toString()}`);
        setServerActions(items);
      } catch (error) {
        console.error('Error fetching actions:', error);
      } finally {
        setLoading(false);
      }
    },
    [get],
  );

  const addAction = useCallback((action: Action) => {
    setLocalActions((prevLocalActions) => {
      if (action.id && prevLocalActions.some((a) => a.id === action.id)) {
        return prevLocalActions;
      }

      const localAction: LocalAction = {
        ...action,
        id: action.id || `local_${Date.now()}`,
      };
      return [...prevLocalActions, localAction];
    });

    setSelectedActions((prev) => {
      const actionId = action.id || `local_${Date.now()}`;
      if (prev.includes(actionId)) {
        return prev;
      }
      return [...prev, actionId];
    });
  }, []);

  const updateAction = useCallback(
    (updatedAction: Action) => {
      setLocalActions((prev) => {
        const existingLocalAction = prev.find(
          (action) => action.id === updatedAction.id,
        );

        if (existingLocalAction) {
          // Update existing local action
          return prev.map((action) =>
            action.id === updatedAction.id ? { ...updatedAction } : action,
          );
        }

        // Only create new local action if updating a server action
        const isServerAction = serverActions.some(
          (action) => action.id === updatedAction.id,
        );

        if (isServerAction) {
          const newActionId = `local_${Date.now()}`;
          const newLocalAction: LocalAction = {
            ...updatedAction,
            id: newActionId,
            originalId: updatedAction.id,
          };

          setSelectedActions((prev) => [
            ...prev.filter((id) => id !== updatedAction.id),
            newActionId,
          ]);

          return [...prev, newLocalAction];
        }

        // If it's neither local nor server, just add it
        return [...prev, { ...updatedAction }];
      });
    },
    [serverActions],
  );

  const getActionById = useCallback(
    async (id: string) => {
      const localAction = localActions.find((action) => action.id === id);
      if (localAction) return localAction;

      const serverAction = serverActions.find((action) => action.id === id);
      if (serverAction) return serverAction;

      const fetchedAction = await get(`/actions/${id}`);

      if (fetchedAction) {
        return fetchedAction;
      }

      return undefined;
    },
    [localActions, serverActions, get],
  );

  const filterActions = useCallback(
    (filter: string) => {
      fetchActions(filter);
    },
    [fetchActions],
  );

  const populateActions = () => {
    const filteredServerActions = serverActions.filter(
      (serverAction) =>
        !localActions.some(
          (localAction) =>
            localAction.originalId === serverAction.id ||
            localAction.id === serverAction.id,
        ),
    );

    const localActionsFiltered =
      filter?.length > 0
        ? localActions.filter((action) => action.label?.includes(filter))
        : localActions;
    const actions = [...localActionsFiltered, ...filteredServerActions];
    setGlobalActions(actions);
  };

  useEffect(() => {
    populateActions();
  }, [localActions, serverActions, filter]);

  useEffect(() => {
    fetchActions();
  }, []);

  const getActionsToSave = useCallback((): (
    | string
    | (Action & { originalId?: string })
  )[] => {
    const filteredServerActions = serverActions.filter(
      (serverAction) =>
        !localActions.some(
          (localAction) =>
            localAction.originalId === serverAction.id ||
            localAction.id === serverAction.id,
        ),
    );

    const actionsPreparedToSave = [...localActions, ...filteredServerActions];

    const allActionsToSave = actionsPreparedToSave.filter(
      (action: LocalAction) =>
        selectedActions.includes(action.id || action.originalId || ''),
    );

    return allActionsToSave.map((action: LocalAction) => {
      if (!action.originalId && !action.id.startsWith('local_')) {
        return action.id;
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, originalId, ...rest } = action;
      return rest as Action & { originalId?: string };
    });
  }, [serverActions, localActions, selectedActions]);

  const resetActions = useCallback(() => {
    setLocalActions([]);
    setSelectedActions([]);
  }, []);

  return (
    <AgentActionsContext.Provider
      value={{
        selectedActions,
        loading,
        fetchActions,
        addAction,
        updateAction,
        setSelectedActions,
        getActionById,
        globalActions,
        filterActions,
        getActionsToSave,
        resetActions,
      }}
    >
      {children}
    </AgentActionsContext.Provider>
  );
}

export function useAgentActions() {
  const context = useContext(AgentActionsContext);

  if (!context) {
    throw new Error(
      'useAgentActions must be used within an AgentActionsProvider',
    );
  }

  return context;
}
